import { useEffect, useState } from "react";
import { useRoute, useLocation, Link } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Crown, CheckCircle2, XCircle } from "lucide-react";

const TIER_LABELS: Record<string, string> = {
  starter: "Starter",
  growth: "Growth",
  dominator: "Dominator",
};

export const REDEEM_STORAGE_KEY = "dd_redeem_code";

export default function RedeemPage() {
  const [, params] = useRoute("/redeem/:code");
  const [, navigate] = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const code = (params?.code || "").toUpperCase();

  const [state, setState] = useState<
    | { kind: "checking" }
    | { kind: "invalid"; reason?: string }
    | { kind: "claimable"; tier: string; label?: string | null }
    | { kind: "claiming" }
    | { kind: "claimed"; tier: string }
    | { kind: "error"; message: string }
  >({ kind: "checking" });

  // Validate the code up-front (public, no login needed).
  useEffect(() => {
    if (!code) {
      setState({ kind: "invalid", reason: "not_found" });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/redeem/${encodeURIComponent(code)}`, { credentials: "include" });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || !data.valid) {
          setState({ kind: "invalid", reason: data.reason });
        } else {
          setState({ kind: "claimable", tier: data.tier, label: data.label });
        }
      } catch {
        if (!cancelled) setState({ kind: "invalid", reason: "not_found" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const claim = async () => {
    setState({ kind: "claiming" });
    try {
      const res = await apiRequest("POST", `/api/redeem/${encodeURIComponent(code)}`);
      const data = await res.json();
      try {
        localStorage.removeItem(REDEEM_STORAGE_KEY);
      } catch {}
      await queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      setState({ kind: "claimed", tier: data.tier });
    } catch (err: any) {
      setState({ kind: "error", message: err?.message || "Could not claim this invite." });
    }
  };

  // Once we know the code is good: if logged in, claim automatically; if not,
  // stash the code and send them to sign up — they'll be redeemed on return.
  useEffect(() => {
    if (authLoading) return;
    if (state.kind !== "claimable") return;
    if (isAuthenticated) {
      claim();
    } else {
      try {
        localStorage.setItem(REDEEM_STORAGE_KEY, code);
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, state.kind]);

  const tierName = (t: string) => TIER_LABELS[t] || t;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 p-4">
      <Card className="w-full max-w-md border-border/60" data-testid="card-redeem">
        <CardContent className="p-8 text-center">
          <div className="mx-auto mb-5 w-14 h-14 rounded-2xl bg-amber-500/15 flex items-center justify-center">
            <Crown className="w-7 h-7 text-amber-500" />
          </div>

          {state.kind === "checking" && (
            <div className="flex flex-col items-center gap-3" data-testid="status-redeem-checking">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Checking your invite…</p>
            </div>
          )}

          {state.kind === "invalid" && (
            <div className="flex flex-col items-center gap-3" data-testid="status-redeem-invalid">
              <XCircle className="w-8 h-8 text-red-400" />
              <h1 className="text-lg font-semibold">This invite link isn't available</h1>
              <p className="text-sm text-muted-foreground">
                {state.reason === "exhausted"
                  ? "This link has reached its redemption limit."
                  : "The link may be expired, deactivated, or incorrect."}
              </p>
              <Link href="/">
                <Button variant="outline" className="mt-2" data-testid="button-redeem-home">Go to homepage</Button>
              </Link>
            </div>
          )}

          {(state.kind === "claimable" && !isAuthenticated) && (
            <div className="flex flex-col items-center gap-3" data-testid="status-redeem-signup">
              <h1 className="text-xl font-bold">You've been invited 🎉</h1>
              <p className="text-sm text-muted-foreground">
                Claim <span className="font-semibold text-foreground">{tierName(state.tier)}</span> access free —
                no credit card required. Create your account and it'll unlock automatically.
              </p>
              <Button
                className="mt-2 w-full"
                onClick={() => navigate("/login")}
                data-testid="button-redeem-signup"
              >
                Sign up to claim
              </Button>
              <p className="text-xs text-muted-foreground">
                Already have an account?{" "}
                <button className="underline" onClick={() => navigate("/login")} data-testid="link-redeem-login">
                  Log in
                </button>
              </p>
            </div>
          )}

          {(state.kind === "claiming" || (state.kind === "claimable" && isAuthenticated)) && (
            <div className="flex flex-col items-center gap-3" data-testid="status-redeem-claiming">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Unlocking your access…</p>
            </div>
          )}

          {state.kind === "claimed" && (
            <div className="flex flex-col items-center gap-3" data-testid="status-redeem-claimed">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              <h1 className="text-xl font-bold">{tierName(state.tier)} unlocked!</h1>
              <p className="text-sm text-muted-foreground">
                Your account now has full {tierName(state.tier)} access. Enjoy!
              </p>
              <Button className="mt-2 w-full" onClick={() => navigate("/dashboard")} data-testid="button-redeem-dashboard">
                Go to dashboard
              </Button>
            </div>
          )}

          {state.kind === "error" && (
            <div className="flex flex-col items-center gap-3" data-testid="status-redeem-error">
              <XCircle className="w-8 h-8 text-red-400" />
              <h1 className="text-lg font-semibold">Something went wrong</h1>
              <p className="text-sm text-muted-foreground">{state.message}</p>
              <Button variant="outline" className="mt-2" onClick={claim} data-testid="button-redeem-retry">
                Try again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
